import PropTypes from 'prop-types';
import {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from "@mui/material/Button";
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

import useUser from '../../../hooks/useUser';
import LoadingScreen from '../../common/LoadingScreen';
import AuthLayout from "../../auth/AuthLayout";
import AppointmentSummary from '../../scheduling/AppointmentSummary';
import FormLayout from "../FormLayout";
import "./PatientSignupLayout.scss"

const PatientSignupLayout = props => {
  const {
    step,
    numSteps,
    children,
    hasPadding = true,
    showStep = true,
    stepTitle = 'Create an account to get started with your first session',
    displayAppointmentSummary = false,
  } = props;
  // Providers cannot view this page
  const navigate = useNavigate();
  const user = useUser();
  const invalidUser = user?.is_provider;

  useEffect(() => {
    if (user?.is_provider) navigate('/');
    if (
      user?.is_patient 
      && user?.patient?.completed_onboarding
    ) navigate('/clients/onboarding/completed');
  }, [navigate, user]);

  if (invalidUser) return <LoadingScreen/>;

  const Wrapper = props.useLayout ? FormLayout : Stack;

  return (
      <AuthLayout {...props} hasWrapperContent={false}>
        <Stack className='PatientSigunLayout' flexDirection='column' width='100%' >
          <Stack
            className="stepper-header"
            direction='column'
            alignItems='center'
            padding='0 0 20px 0'
          >
            <Stack
              width={{xs: '100%'}}
              direction='column'
              justifyContent='center'
              alignItems='center'
              gap={1}
            >
              <Stack width='100%' padding={{xs: '0 8.5px', sm: 'auto'}}>
                <Typography
                  variant='large'
                  lineHeight='1.406rem'
                  letterSpacing='0.4px'
                  textAlign='center'
                  color='white'
                >
                  {stepTitle}
                </Typography>

                {step && showStep && (
                  <Typography
                    variant='large'
                    lineHeight='1.406rem'
                    fontWeight={300}
                    textAlign='center'
                    color='white'
                  >
                    Step {step} of {numSteps}
                  </Typography>
                )}
              </Stack>

              <LinearProgress className="stepper-header__progress" variant='determinate' value={(step / numSteps) * 100}/>
            </Stack>
          </Stack>

          <Stack
            width='100%'
            overflow='auto'
            direction='column'
            justifyContent='center'
            alignItems='center'
            padding={hasPadding && '0 10px'}
          >
            <Wrapper>
              {
                props.onBack && !props.hideBackButton
                  && (
                  <Button onClick={props.onBack} sx={{alignSelf: 'flex-start'}}>
                    <Typography variant='large' lineHeight='1.406rem'>{'<'} Back</Typography>
                  </Button>
                )
              }
              {displayAppointmentSummary && props.selectedProvider &&
                <Box marginTop={1} marginBottom={2}>
                  <AppointmentSummary
                    className="ContactInfo__appointment-summary"
                    provider={props.selectedProvider}
                    sessionType={props.sessionType}
                    date={props.date}
                    time={props.time}
                    scheduleManually={props?.scheduleManually}
                  />
                </Box>
              }
              {props.isLoading
                ? <Stack direction='row' justifyContent='center' padding={2}>
                  <CircularProgress/>
                </Stack>
                : children
              }
            </Wrapper>
          </Stack>
        </Stack>
      </AuthLayout>
  );
};

PatientSignupLayout.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  buttonText: PropTypes.string,
  step: PropTypes.number,
  numSteps: PropTypes.number,
  backgroundColor: PropTypes.string,
  hasPadding: PropTypes.bool
};

export default PatientSignupLayout;
